/**
 * The three ways out (docs/SPEC.md §13, §15, §30.5; P24-T05), opened by
 * **Approve**: three cards, each with its price and its time from
 * `GET …/outputs/estimate`.
 *
 * 1. **The real-time film** — chosen at first; 720p or 1080p.
 * 2. **The ray-traced final** — at the estimate's resolution; **See one
 *    moment first** asks for a true render of the paused moment (§13.2).
 * 3. **A generative film elsewhere** — **Copy the scene's description** puts
 *    the text on the clipboard and offers the scenes' pictures, drawn and
 *    zipped in the browser (`FramesZip`, P28-T10); and
 *    **Refine with {provider}** only when the estimate names a refinement
 *    provider (§30.6, P26-T04: `refine` is null while none is set, so it is
 *    hidden in this version).
 *
 * A language other than the film's own is offered when the version holds a
 * sibling (`languages`, own first); it applies to all three.
 *
 * Prices (§15.1, P28-T08): every GPU render asks first — its make button
 * opens "{The way} — about N credits. Go ahead?" and only **Go ahead** sends
 * it (with `confirm: true`); the real-time film on a CPU node is free and
 * starts at once; a balance below the estimate is the block with **Top up**.
 */
import { GenerationPackage } from "./GenerationPackage";
import { type ReactNode, useState } from "react";
import { ApiError, shortfallOf } from "../api/client";
import { PriceConfirm, TopUp } from "../ui/Credits";
import {
  type Description,
  type Output,
  fetchDescription,
  useMakeOutput,
  useOutputsEstimate,
} from "../api/outputs";
import { WAYS_OUT, outputError, say } from "../copy/outputs";
import { Button, Card, Skeleton } from "../ui";
import { FramesZip } from "./FramesZip";

export type Way = "rt" | "final" | "elsewhere";
export const RT_RESOLUTIONS = [720, 1080] as const;

const CARD =
  "flex min-w-0 flex-col gap-3 rounded-lg border p-4 text-sm transition-colors";

/** One way out: a row with its name, what it is, its price and its time;
 *  chosen, its controls under it. */
function Choice({
  way,
  chosen,
  title,
  price,
  time,
  body,
  onChoose,
  children,
}: {
  way: Way;
  chosen: boolean;
  title: string;
  price: string;
  time: string;
  body: string;
  onChoose(): void;
  children?: ReactNode;
}) {
  return (
    <div
      className={`${CARD} ${chosen ? "border-pink-500 bg-pink-50/40 shadow-sm" : "border-slate-200 bg-white"}`}
      data-testid={`way-${way}`}
      data-chosen={chosen}
    >
      <label className="flex cursor-pointer flex-wrap items-start gap-x-4 gap-y-1">
        <span className="flex min-w-0 flex-1 basis-64 items-start gap-2">
          <input
            type="radio"
            name="way-out"
            value={way}
            checked={chosen}
            onChange={onChoose}
            className="mt-1 shrink-0 accent-pink-600"
          />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="font-semibold text-slate-900">{title}</span>
            <span className="text-slate-600">{body}</span>
          </span>
        </span>
        <span className="ml-6 flex shrink-0 flex-col sm:ml-0 sm:items-end">
          <span
            className="font-semibold text-slate-800"
            data-testid={`price-${way}`}
          >
            {price}
          </span>
          <span className="text-xs text-slate-500" data-testid={`time-${way}`}>
            {time}
          </span>
        </span>
      </label>
      {chosen ? (
        <div className="ml-6 flex flex-col items-start gap-2">{children}</div>
      ) : null}
    </div>
  );
}

export function WaysOut({
  adId,
  version,
  languages,
  onClose,
  onStarted,
  onMoment,
  copyText = (text) => navigator.clipboard.writeText(text),
}: {
  adId: string;
  /** The version the ways out are made from (the current one). */
  version: number;
  /** The film's languages that can go out: its own first, then siblings. */
  languages: string[];
  onClose(): void;
  onStarted?(output: Output): void;
  /** See one moment first: a true render of the paused moment. */
  onMoment(): void;
  /** The clipboard (a test gives its own). */
  copyText?: (text: string) => Promise<void>;
}) {
  const estimate = useOutputsEstimate(adId, true);
  const make = useMakeOutput(adId);
  const [way, setWay] = useState<Way>("rt");
  const [resolution, setResolution] = useState<number>(1080);
  const [language, setLanguage] = useState<string>(languages[0] ?? "");
  const [described, setDescribed] = useState<Description | null>(null);
  const [copied, setCopied] = useState<boolean | null>(null);
  const [describing, setDescribing] = useState(false);
  const [describeError, setDescribeError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);

  const data = estimate.data;
  const lang = languages.length > 1 ? language : undefined;
  const credits = (n: number) =>
    n > 0 ? say(WAYS_OUT.credits, { credits: n }) : WAYS_OUT.free;
  const priceOf = (kind: "rt" | "final"): number =>
    !data
      ? 0
      : kind === "rt"
        ? (data.rt[String(resolution)] ?? 0)
        : data.final.credits;
  // §15.1: every GPU render is confirmed with its estimate first; the real-
  // time film on a CPU node is free and starts at once
  const [asked, setAsked] = useState<"rt" | "final" | null>(null);
  const short = shortfallOf(make.error);

  const start = (kind: "rt" | "final") => {
    if (make.isPending || !data) return;
    setAsked(null);
    setStarted(false);
    make.mutate(
      {
        kind,
        resolution: kind === "rt" ? resolution : data.final.resolution,
        ...(lang ? { language: lang } : {}),
      },
      {
        onSuccess: ({ outputs }) => {
          setStarted(true);
          if (outputs[0]) onStarted?.(outputs[0]);
        },
      },
    );
  };
  const ask = (kind: "rt" | "final") => {
    if (make.isPending || !data) return;
    make.reset();
    setStarted(false);
    if (priceOf(kind) > 0) {
      setAsked(kind);
    } else {
      start(kind);
    }
  };
  const confirm = (kind: "rt" | "final") =>
    asked === kind ? (
      <PriceConfirm
        what={kind}
        credits={priceOf(kind)}
        busy={make.isPending}
        onYes={() => start(kind)}
        onNo={() => setAsked(null)}
      />
    ) : null;

  const describe = async () => {
    setDescribing(true);
    setDescribeError(null);
    setCopied(null);
    try {
      const got = await fetchDescription(adId, lang);
      setDescribed(got);
      try {
        await copyText(got.text);
        setCopied(true);
      } catch {
        setCopied(false);
      }
    } catch (error) {
      setDescribeError(
        error instanceof ApiError
          ? outputError(error)
          : WAYS_OUT.elsewhere.failed,
      );
    } finally {
      setDescribing(false);
    }
  };

  const minutes = data ? Math.max(1, Math.round(data.final.seconds / 60)) : 0;

  return (
    <Card
      className="flex flex-col gap-4 p-4 md:p-5"
      role="region"
      aria-label={WAYS_OUT.title}
      data-testid="ways-out"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h2 className="m-0 text-base font-semibold text-slate-900">
            {WAYS_OUT.title}
          </h2>
          <p className="m-0 text-sm text-slate-600">
            {say(WAYS_OUT.intro, { n: data?.version ?? version })}
          </p>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          {WAYS_OUT.close}
        </Button>
      </div>

      {languages.length > 1 ? (
        <label className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
          {WAYS_OUT.language}
          <select
            className="tb-input w-auto py-1"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            data-testid="way-language"
          >
            {languages.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {estimate.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <Skeleton height={72} />
          <Skeleton height={72} />
          <Skeleton height={72} />
        </div>
      ) : estimate.isError || !data ? (
        <div className="flex flex-wrap items-center gap-3" role="alert">
          <p className="m-0 text-sm text-slate-700">{WAYS_OUT.pricesFailed}</p>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => void estimate.refetch()}
          >
            {WAYS_OUT.retry}
          </Button>
        </div>
      ) : (
        <div
          className="flex flex-col gap-3"
          role="radiogroup"
          aria-label={WAYS_OUT.title}
        >
          <Choice
            way="rt"
            chosen={way === "rt"}
            title={WAYS_OUT.rt.title}
            price={credits(data.rt[String(resolution)] ?? 0)}
            time={WAYS_OUT.rt.time}
            body={WAYS_OUT.rt.body}
            onChoose={() => setWay("rt")}
          >
            <fieldset className="m-0 flex flex-wrap items-center gap-3 border-0 p-0">
              <legend className="sr-only">{WAYS_OUT.resolution}</legend>
              {RT_RESOLUTIONS.map((r) => (
                <label
                  key={r}
                  className="flex items-center gap-1 text-slate-700"
                >
                  <input
                    type="radio"
                    name="rt-resolution"
                    value={r}
                    checked={resolution === r}
                    onChange={() => setResolution(r)}
                    className="accent-pink-600"
                  />
                  {r}p · {credits(data.rt[String(r)] ?? 0)}
                </label>
              ))}
            </fieldset>
            <Button
              type="button"
              variant="primary"
              disabled={make.isPending}
              aria-busy={make.isPending}
              onClick={() => ask("rt")}
            >
              {priceOf("rt") > 0
                ? say(WAYS_OUT.rt.make, { credits: priceOf("rt") })
                : WAYS_OUT.rt.makeFree}
            </Button>
            {confirm("rt")}
          </Choice>
          <Choice
            way="final"
            chosen={way === "final"}
            title={WAYS_OUT.final.title}
            price={credits(data.final.credits)}
            time={say(WAYS_OUT.final.time, { minutes })}
            body={say(WAYS_OUT.final.body, {
              resolution: String(data.final.resolution),
            })}
            onChoose={() => setWay("final")}
          >
            <Button type="button" variant="secondary" onClick={onMoment}>
              {WAYS_OUT.final.moment}
            </Button>
            <span className="text-xs text-slate-500">
              {say(WAYS_OUT.final.momentHint, { credits: data.still.credits })}
            </span>
            <Button
              type="button"
              variant="primary"
              disabled={make.isPending}
              aria-busy={make.isPending}
              onClick={() => ask("final")}
            >
              {say(WAYS_OUT.final.make, { credits: data.final.credits })}
            </Button>
            {confirm("final")}
          </Choice>
          <Choice
            way="elsewhere"
            chosen={way === "elsewhere"}
            title={WAYS_OUT.elsewhere.title}
            price={WAYS_OUT.free}
            time={WAYS_OUT.elsewhere.time}
            body={WAYS_OUT.elsewhere.body}
            onChoose={() => setWay("elsewhere")}
          >
            <GenerationPackage adId={adId} version={version} language={lang} />
            <Button
              type="button"
              variant="secondary"
              disabled={describing}
              aria-busy={describing}
              onClick={() => void describe()}
            >
              {describing
                ? WAYS_OUT.elsewhere.copying
                : WAYS_OUT.elsewhere.copy}
            </Button>
            {described && copied ? (
              <p className="m-0 text-slate-700" role="status">
                {described.sentence}
              </p>
            ) : null}
            {described && copied === false ? (
              <>
                <p className="m-0 text-slate-700" role="status">
                  {WAYS_OUT.elsewhere.manual}
                </p>
                <textarea
                  readOnly
                  aria-label={WAYS_OUT.elsewhere.textLabel}
                  className="tb-input h-32 w-full font-mono text-xs"
                  value={described.text}
                  onFocus={(e) => e.currentTarget.select()}
                />
              </>
            ) : null}
            {described ? (
              <FramesZip adId={adId} version={described.version} />
            ) : null}
            {describeError ? (
              <p className="m-0 text-slate-700" role="alert">
                {describeError}
              </p>
            ) : null}
            {data.refine ? (
              <div
                className="flex flex-col items-start gap-1 border-t border-slate-200 pt-2"
                data-testid="refine"
              >
                <Button type="button" variant="secondary" disabled>
                  {say(WAYS_OUT.elsewhere.refine, data.refine)}
                </Button>
                <span className="text-xs text-slate-500">
                  {say(WAYS_OUT.elsewhere.refineHint, data.refine)}
                </span>
              </div>
            ) : null}
          </Choice>
        </div>
      )}

      {short ? (
        <TopUp shortfall={short} />
      ) : make.isError ? (
        <p className="m-0 text-sm text-slate-700" role="alert">
          {outputError(make.error)}
        </p>
      ) : null}
      {started ? (
        <p className="m-0 text-sm text-slate-700" role="status">
          {WAYS_OUT.started}
        </p>
      ) : null}
    </Card>
  );
}
